import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Smartphone, Building2, Copy, Check, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { processImageToFile, resolveImageUrl } from '../lib/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Payment = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [copied, setCopied] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchBooking();
    fetchPaymentSettings();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Failed to load booking details');
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings`);
      setPaymentSettings(response.data);
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const handleCopyBankDetails = () => {
    if (paymentSettings?.bank_details) {
      navigator.clipboard.writeText(paymentSettings.bank_details);
      setCopied(true);
      toast.success('Bank details copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const processed = await processImageToFile(file);
      setProofFile(processed);
      setProofPreview(URL.createObjectURL(processed));
    } catch (err) {
      toast.error(err.message || 'Failed to process image');
    }
  };

  const handleRemoveProof = () => {
    setProofFile(null);
    setProofPreview(null);
  };

  const handlePaymentComplete = async () => {
    if (!proofFile) {
      toast.error('Please upload a screenshot of your payment confirmation');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', proofFile);
      await axios.post(`${API}/bookings/${bookingId}/proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Payment screenshot submitted! Redirecting...');
      setTimeout(() => {
        navigate(`/confirmation/${bookingId}`);
      }, 1200);
    } catch (error) {
      console.error('Error uploading proof:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload screenshot');
      setUploading(false);
    }
  };

  if (!booking || !paymentSettings) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <p className="text-xl">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-12" data-testid="payment-header">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
            COMPLETE PAYMENT
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-zinc-600">
            Choose your preferred payment method below
          </p>
        </div>

        {/* Booking Summary */}
        <div className="bg-zinc-50 border border-zinc-200 p-6 mb-8" data-testid="booking-summary">
          <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
            Booking Summary
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-600">Booking ID:</span>
              <span className="font-medium">{booking.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-600">Name:</span>
              <span className="font-medium">{booking.customer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-600">Type:</span>
              <span className="font-medium capitalize">{booking.booking_type}</span>
            </div>
            {booking.item_name && (
              <div className="flex justify-between">
                <span className="text-zinc-600">{booking.booking_type === 'pass' ? 'Pass' : (booking.booking_type === 'studio' ? 'Studio' : (booking.booking_type === 'event' ? 'Event' : 'Class'))}:</span>
                <span className="font-medium" data-testid="payment-item-name">{booking.item_name}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-zinc-600">Date:</span>
              <span className="font-medium">{booking.booking_date}</span>
            </div>
            {booking.booking_time && (
              <div className="flex justify-between">
                <span className="text-zinc-600">Time:</span>
                <span className="font-medium">{booking.booking_time}</span>
              </div>
            )}
            {booking.pax ? (
              <div className="flex justify-between">
                <span className="text-zinc-600">Group Size:</span>
                <span className="font-medium">{booking.pax} pax</span>
              </div>
            ) : null}
            {booking.hours ? (
              <div className="flex justify-between">
                <span className="text-zinc-600">Duration:</span>
                <span className="font-medium">{booking.hours} hour(s)</span>
              </div>
            ) : null}
            {booking.selected_addons && booking.selected_addons.length > 0 ? (
              <div className="pt-2 mt-2 border-t border-zinc-200" data-testid="payment-addons">
                <span className="text-zinc-600 block mb-1">Add-ons:</span>
                {booking.selected_addons.map((a, i) => (
                  <div key={i} className="flex justify-between pl-3">
                    <span className="text-zinc-600">• {a.name}</span>
                    <span className="font-medium">{a.price ? `₱${Number(a.price).toLocaleString('en-PH')}` : 'Free'}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {booking.price ? (
              <div className="flex justify-between pt-2 mt-2 border-t border-zinc-200">
                <span className="font-semibold">Total Amount:</span>
                <span className="font-bold text-lg" data-testid="payment-total-amount">₱{Number(booking.price).toLocaleString('en-PH')}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Payment Methods */}
        <Tabs defaultValue="gcash" className="w-full" data-testid="payment-tabs">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="gcash" className="flex items-center gap-2" data-testid="tab-gcash">
              <Smartphone size={18} />
              GCash
            </TabsTrigger>
            <TabsTrigger value="bank" className="flex items-center gap-2" data-testid="tab-bank">
              <Building2 size={18} />
              Bank Transfer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="gcash" className="space-y-6" data-testid="gcash-content">
            <div className="border border-zinc-200 p-8 text-center">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'var(--heading-font)' }}>
                Scan GCash QR Code
              </h3>
              <div className="flex justify-center mb-6">
                <img
                  src={resolveImageUrl(paymentSettings.gcash_qr)}
                  alt="GCash QR Code"
                  className="max-w-xs border-4 border-[var(--accent)]"
                  data-testid="gcash-qr-image"
                />
              </div>
              {paymentSettings.gcash_details && (
                <div className="bg-zinc-50 p-4 mb-4 text-left max-w-md mx-auto" data-testid="gcash-details">
                  <pre className="whitespace-pre-wrap font-mono text-sm">{paymentSettings.gcash_details}</pre>
                </div>
              )}
              <p className="text-zinc-600 mb-4">
                Open your GCash app and scan this QR code to complete payment
              </p>
              <p className="text-sm text-zinc-500">
                After payment, click the button below to confirm
              </p>
            </div>
          </TabsContent>

          <TabsContent value="bank" className="space-y-6" data-testid="bank-content">
            <div className="border border-zinc-200 p-8">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'var(--heading-font)' }}>
                Bank Transfer Details
              </h3>
              <div className="bg-zinc-50 p-6 mb-6 relative">
                <pre className="whitespace-pre-wrap font-mono text-sm mb-4">
                  {paymentSettings.bank_details}
                </pre>
                <button
                  onClick={handleCopyBankDetails}
                  className="absolute top-4 right-4 p-2 hover:bg-zinc-200 rounded transition-colors"
                  data-testid="copy-bank-details-btn"
                >
                  {copied ? <Check size={20} className="text-green-600" /> : <Copy size={20} />}
                </button>
              </div>
              <p className="text-zinc-600 mb-4">
                Transfer the payment to the account above and save your transaction reference
              </p>
              <p className="text-sm text-zinc-500">
                After completing the transfer, click the button below to confirm
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Upload Payment Proof */}
        <div className="mt-8 border-2 border-dashed border-zinc-300 p-6" data-testid="proof-upload-section">
          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>
            Upload Payment Confirmation <span className="text-red-500">*</span>
          </h3>
          <p className="text-sm text-zinc-600 mb-4">
            Attach a screenshot of your GCash / QR PH / bank transfer receipt so our team can verify your payment.
          </p>

          {!proofPreview ? (
            <label
              htmlFor="proof-upload-input"
              className="flex flex-col items-center justify-center gap-3 py-10 cursor-pointer bg-zinc-50 hover:bg-zinc-100 transition-colors border border-zinc-200"
              data-testid="proof-upload-label"
            >
              <Upload size={32} className="text-zinc-500" />
              <span className="text-sm font-medium text-zinc-700">Click to upload screenshot</span>
              <span className="text-xs text-zinc-400">PNG, JPG up to 16MB</span>
              <input
                id="proof-upload-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
                data-testid="proof-upload-input"
              />
            </label>
          ) : (
            <div className="relative inline-block" data-testid="proof-preview">
              <img
                src={proofPreview}
                alt="Payment proof preview"
                className="max-h-64 border-2 border-[var(--accent)]"
              />
              <button
                onClick={handleRemoveProof}
                className="absolute -top-3 -right-3 bg-black text-white p-1.5 rounded-full hover:bg-zinc-700 transition-colors"
                data-testid="remove-proof-btn"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Confirm Button */}
        <div className="mt-8 space-y-4">
          <button
            onClick={handlePaymentComplete}
            disabled={uploading}
            className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-8 py-6 text-lg font-bold transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            data-testid="confirm-payment-btn"
          >
            {uploading ? 'SUBMITTING...' : "I'VE COMPLETED THE PAYMENT"}
          </button>
          <p className="text-center text-sm text-zinc-500">
            Your booking will be confirmed once we verify your payment screenshot
          </p>
        </div>
      </div>
    </div>
  );
};

export default Payment;